/**
 * Settings → Users and permissions.
 *
 * Everyone who can sign in, what door they use, and a button that opens exactly what
 * they may do. The detail lives in AccessPanel, shared with the employee's own page,
 * so there is one editor and it is wherever someone goes looking.
 *
 * An earlier version hid the permission grid behind an inline "Edit permissions" toggle
 * on each row. It worked, and nobody found it.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Modal } from '@/components/Modal';
import { Avatar, Badge, Card, ErrorNotice, Spinner } from '@/components/ui';
import { useAuth } from '@/features/auth/AuthProvider';
import { useI18n } from '@/i18n';
import { api, type User } from '@/lib/api';
import { useLabel } from '@/lib/useLabel';

import { AccessPanel } from './AccessPanel';
import { usePermissions } from './usePermissions';
import { createErrorText, UserFormFields, useCreateUser } from './UserForm';
import { DeleteUserButton } from './DeleteUserButton';
import { ReinviteButton } from './ReinviteButton';
import { awaitingSignIn } from './signIn';

function fullName(u: User): string {
  return `${u.firstName} ${u.lastName}`.trim();
}

export function UsersAccess() {
  const { t } = useI18n();
  const { email: myEmail } = useAuth();
  const { can } = usePermissions();
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.users() });
  const [creating, setCreating] = useState(false);
  const [managing, setManaging] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  if (users.isLoading) return <Spinner />;
  if (users.error) return <ErrorNotice error={users.error} />;

  const list = users.data?.users ?? [];
  const isSelf = (u: User) => Boolean(u.email) && u.email!.toLowerCase() === (myEmail ?? '').toLowerCase();
  const target = list.find((u) => u.id === managing) ?? null;

  return (
    <div className="max-w-4xl space-y-4">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-bold">{t('access.title')}</h2>
            <p className="mt-1 max-w-xl text-xs leading-relaxed text-muted">{t('access.hint')}</p>
          </div>
          {can('users', 'write') ? (
            <button type="button" className="btn-primary shrink-0" onClick={() => setCreating(true)}>
              {t('access.newUser')}
            </button>
          ) : null}
        </div>

        {notice ? (
          <p className="mt-3 text-sm font-semibold text-success" role="status">
            {notice}
          </p>
        ) : null}

        {list.length === 0 ? (
          <p className="mt-4 text-sm text-muted">{t('access.empty')}</p>
        ) : (
          <ul className="mt-4 divide-y divide-border border-y border-border">
            {list.map((u) => (
              <UserRow key={u.id} user={u} isSelf={isSelf(u)} onManage={() => setManaging(u.id)} />
            ))}
          </ul>
        )}
      </Card>

      <Modal
        open={target !== null}
        onClose={() => setManaging(null)}
        size="md"
        title={target ? fullName(target) : ''}
        description={target ? (target.email ?? undefined) : undefined}
      >
        {target ? <AccessPanel user={target} isSelf={isSelf(target)} /> : null}
      </Modal>

      <CreateUserModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => {
          setCreating(false);
          setNotice(t('access.created'));
          setTimeout(() => setNotice(null), 6000);
        }}
      />
    </div>
  );
}

function UserRow({ user, isSelf, onManage }: { user: User; isSelf: boolean; onManage: () => void }) {
  const { t } = useI18n();
  const L = useLabel();
  const queryClient = useQueryClient();
  const { can } = usePermissions();

  const status = useMutation({
    mutationFn: () => (user.status === 'suspended' ? api.reactivateUser(user.id) : api.suspendUser(user.id)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  const suspended = user.status === 'suspended';

  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <Avatar name={fullName(user)} size={34} />
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold">
            {fullName(user)}
            {isSelf ? <span className="ms-1.5 text-xs font-normal text-subtle">({t('access.you')})</span> : null}
          </div>
          <div className="truncate text-xs text-muted">{user.email}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <Badge tone={user.role === 'admin' ? 'primary' : 'neutral'}>{L('role', user.role).label}</Badge>
        <DoorChip on={user.portalAccess} label={t('access.doorPortalShort')} />
        <DoorChip on={user.appAccess} label={t('access.doorAppShort')} />
        {suspended ? (
          <Badge tone="danger">{L('user', 'suspended').label}</Badge>
        ) : awaitingSignIn(user) ? (
          <Badge tone="warning">{t(user.signIn === 'none' ? 'access.noSignIn' : 'access.invited')}</Badge>
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        <button type="button" className="btn-secondary text-xs" onClick={onManage}>
          {t('access.manage')}
        </button>
        {awaitingSignIn(user) && can('users', 'write') ? <ReinviteButton userId={user.id} compact /> : null}
        {/* Nobody suspends themselves: that is a sign-out with extra steps and no way back in. */}
        {isSelf || !can('users', 'write') ? null : (
          <button
            type="button"
            className="text-xs font-semibold text-muted hover:text-danger disabled:opacity-60"
            disabled={status.isPending}
            onClick={() => status.mutate()}
          >
            {suspended ? t('access.reactivate') : t('access.suspend')}
          </button>
        )}
        {isSelf || !can('users', 'write') ? null : <DeleteUserButton user={user} compact />}
      </div>
    </li>
  );
}

/** A door, read-only: filled when open, struck through when closed. */
export function DoorChip({ on, label }: { on: boolean; label: string }) {
  return (
    <span
      className={[
        'inline-flex h-6 items-center rounded-full border px-2 text-[11px] font-semibold',
        on
          ? 'border-[var(--color-border-strong)] bg-[var(--color-primary-soft)] text-primary'
          : 'border-border bg-surface text-subtle line-through',
      ].join(' ')}
    >
      {label}
    </span>
  );
}

function CreateUserModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useI18n();
  const { form, setForm, create, valid } = useCreateUser(onCreated);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={t('access.newUser')}
      description={t('access.newUserHint')}
      footer={
        <>
          <button type="button" className="btn-primary" disabled={!valid} onClick={() => create.mutate()}>
            {create.isPending ? t('common.saving') : t('access.create')}
          </button>
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t('common.cancel')}
          </button>
          {create.error ? (
            <p className="ms-auto text-sm text-danger">{createErrorText(create.error, t('common.saveError'))}</p>
          ) : null}
        </>
      }
    >
      <UserFormFields form={form} setForm={setForm} onSubmit={() => valid && create.mutate()} />
    </Modal>
  );
}
